import { FormControl, FormControlProps, FormHelperText, FormLabel, Stack } from "@mui/material";
import React from "react";
import { FieldPath, FieldValues, useFormState } from "react-hook-form";

import { assertNever } from "../../utils/assertNever";
import { getMaybeErrorMessage } from "./getMaybeErrorMessage";

type FormInputWrapperLabelVariant = "standard" | "small";

interface IProps<TFieldValues extends FieldValues, TName extends FieldPath<TFieldValues>> {
    name: TName;
    required?: boolean;
    disabled?: boolean;
    splitLabelAlignment?: boolean;
    label?: React.ReactNode;
    labelEndAdornment?: React.ReactNode;
    helperText?: React.ReactNode;
    sx?: FormControlProps["sx"];
    labelVariant?: FormInputWrapperLabelVariant;
}

export function FormInputWrapper<
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
    name,
    required,
    disabled,
    splitLabelAlignment,
    label,
    labelEndAdornment,
    helperText,
    sx,
    labelVariant = "standard",
    children,
}: React.PropsWithChildren<IProps<TFieldValues, TName>>): React.ReactElement | null {
    const { errors } = useFormState({ name });
    const errorMessage = getMaybeErrorMessage(errors, name);

    return (
        <FormControl fullWidth={true} required={required} disabled={disabled} error={errorMessage != null} sx={sx}>
            {(label || labelEndAdornment) && (
                <Stack
                    {...(splitLabelAlignment && { justifyContent: "space-between" })}
                    direction="row"
                    spacing={1}
                    sx={{ ...(helperText == null && { mb: 1 }) }}
                >
                    <FormLabel
                        sx={(theme) => ({
                            ...(labelVariant === "standard"
                                ? {}
                                : labelVariant === "small"
                                  ? theme.typography.label1
                                  : assertNever(labelVariant)),
                        })}
                    >
                        {label}
                    </FormLabel>
                    {labelEndAdornment}
                </Stack>
            )}
            {helperText && (
                <FormHelperText error={false} sx={{ mt: 0, mb: 0.5 }}>
                    {helperText}
                </FormHelperText>
            )}
            {React.Children.map(children, (child) => {
                if (React.isValidElement(child)) {
                    return React.cloneElement(child, {
                        // Child props take precedence
                        error: errorMessage != null,
                        ...child.props,
                    });
                }
                return child;
            })}
            {errorMessage && <FormHelperText>{errorMessage}</FormHelperText>}
        </FormControl>
    );
}
